import React, { useEffect, useState } from 'react';
import { ArrowBackIcon, ArrowForwardIcon } from '@chakra-ui/icons';
import { Badge, Box, Flex, IconButton, Table, Tbody, Text, Th, Thead, Tr, useColorModeValue } from '@chakra-ui/react';
import { getFilteredMerchants } from 'api/merchants';
import { MERCHANT } from 'constants/merchantDetails';
import { MERCHANT_TABLE_HEADINGS } from 'modules/merchants/constants/constants';
import AlertMessage from 'components/AlertMessage/AlertMessage';
import Card from 'components/Card/Card';
import CardBody from 'components/Card/CardBody';
import CardHeader from 'components/Card/CardHeader';
import Loader from 'components/Loader/Loader';
import Search from 'components/SearchBox/Search';
import MerchantRow from 'components/Tables/MerchantRow';

const Merchants = ({ isMerchantClickable = true }) => {
  const textColor = useColorModeValue('gray.700', 'white');
  const [merchantsList, setMerchantsList] = useState([]);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const limit = 10;

  const getMerchants = async ({ newPage = 1, newSearchTerm = '' }) => {
    setIsLoading(true);
    try {
      const response = await getFilteredMerchants({
        filter: newSearchTerm,
        page: newPage,
        limit,
      });

      setMerchantsList(response?.data?.data?.merchants ?? []);
      if (response?.data?.data?.count) {
        setTotalCount(response?.data?.data?.count);
      }
    } catch (error) {
      setError(error);
    }
    setIsLoading(false);
  };

  const handlePreviousPage = () => {
    const newPage = currentPage - 1;

    setCurrentPage(newPage);
    getMerchants({ newPage, newSearchTerm: searchTerm });
  };

  const handleNextPage = () => {
    const newPage = currentPage + 1;

    setCurrentPage(newPage);
    getMerchants({ newPage, newSearchTerm: searchTerm });
  };

  const handleSearch = (newSearchTerm) => {
    setSearchTerm(newSearchTerm);
    setCurrentPage(1);
    getMerchants({ newPage: 1, newSearchTerm });
  };

  const totalPages = Math.ceil(totalCount / limit);

  useEffect(() => {
    getMerchants({});
  }, []);

  return (
    <Flex direction='column' pt='30px' gap={4}>
      <Search searchType={MERCHANT} setSearchTerm={handleSearch} />
      <Card overflowX={{ sm: 'scroll', xl: 'hidden' }}>
        <CardHeader p='6px 0px 22px 0px'>
          <Text fontSize='xl' color={textColor} fontWeight='bold'>
            All Merchants
            <Badge ml={1} colorScheme='blue' variant='solid' px={2}>
              {totalCount}
            </Badge>
          </Text>
        </CardHeader>
        <CardBody>
          <Table color={textColor}>
            <Thead>
              <Tr my='.8rem' pl='0px' color='gray.400'>
                {MERCHANT_TABLE_HEADINGS.map((caption, idx) => {
                  return (
                    <Th color='gray.400' key={idx} ps={idx === 0 ? '0px' : null}>
                      {caption}
                    </Th>
                  );
                })}
              </Tr>
            </Thead>
            <Tbody>
              {merchantsList?.map((row) => {
                return (
                  <MerchantRow
                    key={`${row.code}-${row.name}`}
                    merchantId={row.id}
                    name={row.name}
                    code={row.code}
                    countryCode={row.country_code}
                    isTreasuryEnabled={row.is_treasury_enabled}
                    isPayoutsEnabled={row.is_payouts_enabled}
                    isPaymentsEnabled={row.is_payments_enabled}
                    isMerchantClickable={isMerchantClickable}
                    isOtcEnabled={row.is_otc_enabled}
                  />
                );
              })}
            </Tbody>
          </Table>
        </CardBody>
        {isLoading && <Loader />}
        {error && <AlertMessage errorTitle='Error fetching merchants.' errorMessage={error?.message} />}
      </Card>
      {merchantsList?.length ? (
        <Flex justify='space-between' align='center'>
          <Text>
            Page {currentPage} of {totalPages}
          </Text>
          <Box>
            <IconButton
              icon={<ArrowBackIcon />}
              onClick={handlePreviousPage}
              disabled={currentPage === 1 || isLoading}
            />
            <IconButton
              ml={4}
              icon={<ArrowForwardIcon />}
              onClick={handleNextPage}
              disabled={currentPage === totalPages || isLoading}
            />
          </Box>
        </Flex>
      ) : null}
    </Flex>
  );
};

export default Merchants;
